import React, { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import * as XLSX from "xlsx";
import { batchList } from "../../../api/Batch";
import { useNavigate } from "react-router-dom";
import Master from "../Master";
import {
  batchEnrolledStudentsV0,
  StudentFeeUpdateImportV0,
  StudentImportEnrollOnlyV0,
  StudentPaymentImportV0,
  StudentProfileUpdateImportV0,
} from "../../../api/StudentImport";

const IMPORT_TYPES = {
  FEE_UPDATE_ALL: "enroll_all",
  ENROLL_ONLY: "enroll_only",
  FEE_UPDATE: "fee_update",
  PAYMENT: "payment",
  PROFILE_UPDATE: "profile_update",
};

export default function StudentsImportV5() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [importType, setImportType] = useState("");

  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelSample, setExcelSample] = useState({});
  const [excelData, setExcelData] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [session_id, setSessionId] = useState('');
  // session_id
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [importStatus, setImportStatus] = useState({});
  const [progress, setProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [mappingRows, setMappingRows] = useState([
    { field: "registration", excel_column: "" },
  ]);

  useEffect(() => {
    const storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");

    setSessions(storedSessions);
    setSessionId(storedSessions[0]?.id ?? null);
  }, []);

  const needsBatchForDownload =
    importType === IMPORT_TYPES.ENROLL_ONLY ||
    importType === IMPORT_TYPES.FEE_UPDATE ||
    importType === IMPORT_TYPES.PROFILE_UPDATE ||
    importType === IMPORT_TYPES.FEE_UPDATE_ALL;

  const needsBatchForImport =
    importType === IMPORT_TYPES.ENROLL_ONLY ||
    importType === IMPORT_TYPES.FEE_UPDATE ||
    importType === IMPORT_TYPES.FEE_UPDATE_ALL;

  const getBatchList = async (session_id = "") => {
      setLoading(true);
      try {
          let addUrl = "limit=all";
          if (session_id) {
              addUrl += `session_id=${session_id}`;
              setSessionId(session_id);
      }

      const response = await batchList(addUrl);
      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message || "Failed");
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadProfileTemplate = async () => {
    if (needsBatchForImport && !batchId) {
      return SweetAlertShow("error", "Error", "Please select batch first");
    }

    setLoading(true);

    try {
      const response = await batchEnrolledStudentsV0(`batch_id=${batchId}`);

      if (!response?.data?.status) {
        return SweetAlertShow("error", "Error", response?.data?.message);
      }

      const students = response?.data?.data || [];

      const sheetRows = students.map((r) => ({
        "REGISTRATION NO *": r.registration_no,
        "STUDENT NAME": r.name,
        "FATHER NAME": r.father_name,
        "MOTHER NAME": r.mother_name,
        MOBILE: r.mobile,
        "SECONDARY MOBILE": r.secondary_phone,
        GENDER: r.gender,
        CATEGORY: r.category,
        DOB: r.dob,
        ADDRESS: r.village,
        DISTRICT: r.district,
        STATE: r.state,
        "PIN-CODE": r.pincode,
        EMAIL: r.email,
        AADHAR: r.aadhar,
        "NIC ID": r.nic_id,
        "APAAR ID": r.apaar_id,
        MEDIUM: r.medium,
        "FATHER OCCUPATION": r.father_occupation,
        "FATHER MOBILE": r.father_mobile,
        "FATHER AADHAR": r.father_aadhar,
        "MOTHER MOBILE": r.mother_mobile,
        "MOTHER AADHAR": r.mother_aadhar,
        "CASTE NAME": r.caste_name,
        RELIGION: r.religion,
        "PEN NO": r.pen_no,
        "JAN AADHAR": r.jan_aadhar,
        "ADMISSION CLASS": r.admission_class,
        REMARK: r.remark,
      }));

      const ws = XLSX.utils.json_to_sheet(sheetRows);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Profile Update");

      XLSX.writeFile(wb, `batch_${batchId}_student_profile_update.xlsx`);
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatchList();
  }, []);

  // ---------------------------------------------------------------------
  // Field definitions per sheet type
  // ---------------------------------------------------------------------

  // 1) Student Enroll Only - plain student data, no fee columns at all
  const enrollOnlyFields = [
    { key: "registration", value: "Registration No" },
    { key: "name", value: "Student Name" },
    { key: "father_name", value: "Father Name" },
    { key: "mother_name", value: "Mother Name" },
    { key: "mobile", value: "Mobile" },
    { key: "secondary_phone", value: "Secondary Mobile" },
    { key: "gender", value: "Gender" },
    { key: "category", value: "Category" },
    { key: "dob", value: "DOB" },
    { key: "village", value: "Address" },
    { key: "district", value: "District" },
    { key: "state", value: "State" },
    { key: "pincode", value: "Pin-Code" },
    { key: "rte_status", value: "RTE-Status" },
    { key: "email", value: "Email" },
    { key: "aadhar", value: "Aadhar" },
    { key: "nic_id", value: "NIC ID" },
    { key: "apaar_id", value: "Apaar ID" },
    { key: "medium", value: "Medium" },
    { key: "father_occupation", value: "Father Occupation" },
    { key: "father_mobile", value: "Father Mobile" },
    { key: "father_aadhar", value: "Father Aadhar" },
    { key: "mother_mobile", value: "Mother Mobile" },
    { key: "mother_aadhar", value: "Mother Aadhar" },
    { key: "caste_name", value: "Caste Name" },
    { key: "religion", value: "Religion" },
    { key: "pen_no", value: "PEN No" },
    { key: "jan_aadhar", value: "Jan Aadhar" },
    { key: "admission_class", value: "Admission Class" },
    { key: "admission_date", value: "Admission Date" },
    { key: "remark", value: "Remark" },
    { key: "previous_session_due_fees", value: "Previous Session Due Fees" },
    {
      key: "previous_session_due_fees_remark",
      value: "Previous Session Due Fees Remark",
    },
    {
      key: "is_new",
      value: "Is New Student",
    },
  ];

  // 2) Fee Update for already-enrolled students (sheet is server-generated)
  const feeUpdateFields = [
    { key: "registration", value: "Registration No" },
    { key: "name", value: "Student Name" },
    { key: "mobile", value: "Mobile" },
    { key: "batch_title", value: "Batch Name" },
    { key: "total_fee", value: "Batch Total Fee" },
    { key: "final_fee", value: "Batch Final Fee" },
    { key: "discount_remark", value: "Discount Remark" },
    { key: "previous_session_due_fees", value: "Previous Session Due Fees" },
    {
      key: "previous_session_due_fees_remark",
      value: "Previous Session Due Fees Remark",
    },
  ];

  // 3) Payment transaction upload
  const paymentFields = [
    { key: "registration", value: "Registration No" },
    { key: "name", value: "Student Name" },
    { key: "amount", value: "Amount" },
    { key: "payment_date", value: "Date" },
    { key: "remark", value: "Remark" },
    { key: "bank_account_no", value: "Bank Ac Number" },
    { key: "receipt_no", value: "Receipt No" },
  ];

  const profileUpdateFields = [
    { key: "registration", value: "Registration No" },
    { key: "name", value: "Student Name" },
    { key: "father_name", value: "Father Name" },
    { key: "mother_name", value: "Mother Name" },
    { key: "mobile", value: "Mobile" },
    { key: "secondary_phone", value: "Secondary Mobile" },
    { key: "gender", value: "Gender" },
    { key: "category", value: "Category" },
    { key: "dob", value: "DOB" },
    { key: "village", value: "Address" },
    { key: "district", value: "District" },
    { key: "state", value: "State" },
    { key: "pincode", value: "Pin-Code" },
    { key: "email", value: "Email" },
    { key: "aadhar", value: "Aadhar" },
    { key: "nic_id", value: "NIC ID" },
    { key: "apaar_id", value: "Apaar ID" },
    { key: "medium", value: "Medium" },
    { key: "father_occupation", value: "Father Occupation" },
    { key: "father_mobile", value: "Father Mobile" },
    { key: "father_aadhar", value: "Father Aadhar" },
    { key: "mother_mobile", value: "Mother Mobile" },
    { key: "mother_aadhar", value: "Mother Aadhar" },
    { key: "caste_name", value: "Caste Name" },
    { key: "religion", value: "Religion" },
    { key: "pen_no", value: "PEN No" },
    { key: "jan_aadhar", value: "Jan Aadhar" },
    { key: "admission_class", value: "Admission Class" },
    { key: "admission_date", value: "Admission Date" },
    { key: "remark", value: "Remark" },
  ];

  const activeFields =
    importType === IMPORT_TYPES.FEE_UPDATE
      ? feeUpdateFields
      : importType === IMPORT_TYPES.PAYMENT
        ? paymentFields
        : importType === IMPORT_TYPES.PROFILE_UPDATE
          ? profileUpdateFields
          : enrollOnlyFields;

  const numericFields = ["final_fee", "discount", "total_fee", "amount"];
  const dateFields = ["dob", "admission_date", "payment_date"];
  const readOnlyFields = ["name", "mobile", "batch_title", "total_fee"];

  const fieldAliases = {
    registration: [
      "registration no",
      "sr no",
      "admission no",
      "student reg no",
    ],
    mobile: ["mobile", "mobile number", "phone", "contact"],
    category: ["category", "social category"],
    village: ["address", "habitation", "locality"],
    name: ["name", "student name", "students name"],
    father_name: ["father name", "father"],
    mother_name: ["mother name", "mother"],
    aadhar: ["aadhar", "aadhar number", "aadhar no"],
    nic_id: ["nic id", "student nic id"],
    rte_status: ["rte", "rte_status", "getting free education", "free"],
    father_mobile: ["father mobile", "father phone"],
    secondary_phone: ["phone 2", "secondary mobile"],
    mother_mobile: ["mother mobile", "phone 3"],
    gender: ["gender", "sex"],
    dob: ["dob", "date of birth"],
    admission_class: ["admission class", "adm in class"],
    admission_date: ["admission date", "date of adm"],
    caste_name: ["caste name", "castname"],
    final_fee: ["final fee", "batch final fee", "payable fee"],
    discount_remark: ["discount", "discount remark"],
    total_fee: ["total fee", "batch total fee"],
    fee_remark: ["remark", "fee remark"],
    previous_session_due_fees: ["previous session due fees", "old fees due"],
    previous_session_due_fees_remark: [
      "previous session due fees remark",
      "old fees remark",
    ],
    amount: ["amount", "amt"],
    payment_date: ["date", "payment date"],
    bank_account_no: ["bank ac number", "bank account number", "account no"],
    receipt_no: ["receipt no", "receipt number"],
    batch_title: ["batch name", "batch"],
    is_new: ["is_new", "new_mark", "old_mark", "is new student"],
  };

  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  // ---------------------------------------------------------------------
  // Template download
  // ---------------------------------------------------------------------

  const downloadBlankTemplate = () => {
    const headerMap = {
      [IMPORT_TYPES.ENROLL_ONLY]: [
        "REGISTRATION NO *",
        "STUDENT NAME *",
        "FATHER NAME",
        "MOTHER NAME",
        "MOBILE *",
        "SECONDARY MOBILE",
        "GENDER",
        "CATEGORY",
        "DOB",
        "ADDRESS",
        "DISTRICT",
        "STATE",
        "PIN-CODE",
        "RTE-STATUS",
        "EMAIL",
        "AADHAR",
        "NIC ID",
        "APAAR ID",
        "MEDIUM",
        "FATHER OCCUPATION",
        "FATHER MOBILE",
        "FATHER AADHAR",
        "MOTHER MOBILE",
        "MOTHER AADHAR",
        "CASTE NAME",
        "RELIGION",
        "PEN NO",
        "JAN AADHAR",
        "ADMISSION CLASS",
        "ADMISSION DATE",
        "REMARK",
        "Previous Session Due Fees",
        "Previous Session Due Fees Remark",
        "Is New Student",
      ],
      [IMPORT_TYPES.PAYMENT]: [
        "REGISTRATION NO *",
        "STUDENT NAME",
        "AMOUNT *",
        "DATE *",
        "REMARK",
        "BANK AC NUMBER *",
        "RECEIPT NO",
      ],

      [IMPORT_TYPES.PROFILE_UPDATE]: [
        "REGISTRATION NO *",
        "STUDENT NAME",
        "FATHER NAME",
        "MOTHER NAME",
        "MOBILE",
        "SECONDARY MOBILE",
        "GENDER",
        "CATEGORY",
        "DOB",
        "ADDRESS",
        "DISTRICT",
        "STATE",
        "PIN-CODE",
        "EMAIL",
        "AADHAR",
        "NIC ID",
        "APAAR ID",
        "MEDIUM",
        "FATHER OCCUPATION",
        "FATHER MOBILE",
        "FATHER AADHAR",
        "MOTHER MOBILE",
        "MOTHER AADHAR",
        "CASTE NAME",
        "RELIGION",
        "PEN NO",
        "JAN AADHAR",
        "ADMISSION CLASS",
        "ADMISSION DATE",
        "REMARK",
      ],
    };

    const headers = headerMap[importType];
    if (!headers) return;

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(
      workbook,
      importType === IMPORT_TYPES.PAYMENT
        ? "payment_transaction_template.xlsx"
        : importType === IMPORT_TYPES.PROFILE_UPDATE
          ? "student_profile_update_template.xlsx"
          : "student_enroll_only_template.xlsx",
    );
  };

  const downloadEnrolledTemplate = async () => {
    if (needsBatchForImport && !batchId) {
      return SweetAlertShow("error", "Error", "Please select batch first");
    }

    setLoading(true);
    try {
      const response = await batchEnrolledStudentsV0(
        `batch_id=${batchId}&session_id=${session_id}`,
      );
      if (!response?.data?.status) {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No enrolled students found",
        );
        return;
      }

      const data = response?.data?.data || [];
      if (!data.length) {
        return SweetAlertShow(
          "info",
          "Info",
          "No enrolled students found in this batch",
        );
      }

      const sheetRows = data?.map((r) => ({
        "REGISTRATION NO *": r?.registration_no,
        "STUDENT NAME": r?.name,
        MOBILE: r?.mobile,
        "BATCH NAME": r?.batch_title,
        "BATCH FEE": r?.total_fee,
        "PAYABLE FEE": "",
        "DISCOUNT REMARK": "",
        "PREVIOUS SESSION DUE FEES": "",
        "PREVIOUS SESSION DUE FEES REMARK": "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Update");
      XLSX.writeFile(workbook, `batch_${batchId}_fee_update_template.xlsx`);
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // File upload / auto mapping
  // ---------------------------------------------------------------------
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      SweetAlertShow("error", "Invalid File", "Please upload Excel file");
      return;
    }

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        dateNF: "dd-mm-yyyy",
      });

      if (!jsonData.length) return;

      const headers = (jsonData[0] || [])
        .map((h) => String(h || "").trim())
        .filter(Boolean);

      const rows = jsonData
        .slice(1)
        .filter((row) =>
          row.some(
            (cell) =>
              cell !== undefined && cell !== null && String(cell).trim() !== "",
          ),
        );

      const secondRow = rows[0] || [];

      setExcelHeaders(headers);
      setExcelData(rows);

      const sampleObj = {};
      headers.forEach((h, i) => {
        sampleObj[h] = secondRow[i] || "";
      });
      setExcelSample(sampleObj);

      const autoMap = activeFields
        .map((f) => {
          const possibleNames = [f.value, ...(fieldAliases[f.key] || [])];
          const match = headers.find((h) => {
            const hNorm = normalize(h);
            return possibleNames.some((name) => hNorm === normalize(name));
          });
          if (!match) return null;
          return { field: f.key, excel_column: match };
        })
        .filter(Boolean);

      setMappingRows(
        autoMap.length ? autoMap : [{ field: "", excel_column: "" }],
      );
    };

    reader.readAsArrayBuffer(file);
  };

  const handleAddRow = () => {
    setMappingRows([...mappingRows, { field: "", excel_column: "" }]);
  };

  const handleRemoveRow = (index) => {
    setMappingRows(mappingRows.filter((_, i) => i !== index));
  };

  const handleChange = (index, key, value) => {
    setMappingRows((prev) => {
      let updated = prev.map((row, i) =>
        i === index ? { ...row, [key]: value } : row,
      );

      if (key === "excel_column") {
        updated = updated.map((row, i) =>
          i !== index && row.excel_column === value
            ? { ...row, excel_column: "" }
            : row,
        );
      }
      if (key === "field") {
        updated = updated.map((row, i) =>
          i !== index && row.field === value ? { ...row, field: "" } : row,
        );
      }
      return [...updated];
    });
  };

  const usedFields = mappingRows.map((r) => r.field);

  // ---------------------------------------------------------------------
  // Mapping -> editable rows
  // ---------------------------------------------------------------------
  const getMappedData = () => {
    if (!excelData.length) return [];

    return excelData.map((row) => {
      const obj = {};

      mappingRows.forEach((map) => {
        if (!map.field || !map.excel_column) return;
        const colIndex = excelHeaders.findIndex((h) => h === map.excel_column);
        let value = colIndex !== -1 ? (row[colIndex] ?? "") : "";

        if (dateFields.includes(map.field)) {
          if (typeof value === "number") {
            const excelDate = XLSX.SSF.parse_date_code(value);
            value = `${excelDate.y}-${String(excelDate.m).padStart(2, "0")}-${String(
              excelDate.d,
            ).padStart(2, "0")}`;
          } else if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = `${date.getFullYear()}-${String(
                date.getMonth() + 1,
              ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            }
          }
        }

        if (map.field === "rte_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true"].includes(val) ? "RTE" : "NONRTE";
        }
        if (map.field === "is_new") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true", "new"].includes(val)
            ? "NEW"
            : "OLD";
        }

        if (map.field === "gender") {
          const val = String(value).toLowerCase().trim();
          if (["male", "m", "boy"].includes(val)) value = "Male";
          else if (["female", "f", "girl"].includes(val)) value = "Female";
          else value = "Other";
        }

        if (numericFields.includes(map.field)) {
          value =
            value === "" ? "" : Number(String(value).replace(/,/g, "")) || 0;
        }

        const skipUppercaseFields = [
          "email",
          "dob",
          "mobile",
          "rte_status",
          "gender",
          "payment_date",
          "bank_account_no",
          "receipt_no",
          ...numericFields,
        ];
        if (
          typeof value === "string" &&
          !skipUppercaseFields.includes(map.field)
        ) {
          value = value.trim().toUpperCase();
        }

        obj[map.field] = value;
      });

      return obj;
    });
  };

  const mappedData = useMemo(
    () => getMappedData(),
    [mappingRows, excelData, excelHeaders],
  );

  useEffect(() => {
    setEditableData(mappedData || []);
  }, [mappedData]);

  const handleCellChange = (rowIndex, field, value) => {
    const updated = [...editableData];
    updated[rowIndex][field] = value;
    setEditableData(updated);
  };

  const activeMappings = useMemo(
    () => mappingRows.filter((m) => m.field && m.excel_column),
    [mappingRows],
  );

  // ---------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------
  const validateRow = (row) => {
    const errors = [];

    if (!row.registration) errors.push("Registration No required");

    if (importType === IMPORT_TYPES.ENROLL_ONLY) {
      if (!row.name) errors.push("Name required");
      if (!row.mobile) errors.push("Mobile required");
      if (row.mobile && !/^[0-9]{10}$/.test(String(row.mobile)))
        errors.push("Invalid mobile");
    }

    if (importType === IMPORT_TYPES.PAYMENT) {
      if (!row.amount) errors.push("Amount required");
      if (!row.payment_date) errors.push("Date required");
      if (!row.bank_account_no) errors.push("Bank Ac Number required");
    }

    if (importType === IMPORT_TYPES.PROFILE_UPDATE) {
      if (!row.registration) errors.push("Registration No required");
    }

    return errors;
  };

  const validationErrors = useMemo(
    () => editableData.map((row) => validateRow(row)),
    [editableData, importType],
  );

  // ---------------------------------------------------------------------
  // Import
  // ---------------------------------------------------------------------
  const buildPayload = (row) => {
    if (importType === IMPORT_TYPES.ENROLL_ONLY) {
      return { ...row, batch_id: batchId };
    }
    if (importType === IMPORT_TYPES.FEE_UPDATE) {
      // strip server-generated display-only columns before sending
      const { name, mobile, batch_title, total_fee, ...rest } = row;
      return { ...rest, batch_id: batchId };
    }
    // payment
    return { ...row };
  };

  const runImport = async (rowsToImport, apiFn) => {
    let statusObj = { ...importStatus };
    setLoading(true);
    const chunkSize = 40;

    for (let i = 0; i < rowsToImport.length; i += chunkSize) {
      const chunk = rowsToImport.slice(i, i + chunkSize);
      await Promise.allSettled(
        chunk.map(async (row) => {
          try {
            const res = await apiFn(buildPayload(row.data));
            statusObj[row.index] = {
              status: res?.data?.status ? "success" : "error",
              message: res?.data?.message || "Failed",
            };
          } catch (err) {
            statusObj[row.index] = {
              status: "error",
              message:
                err?.response?.data?.message || err?.message || "Import Failed",
            };
          }
        }),
      );

      setImportStatus({ ...statusObj });
      const processed = Object.keys(statusObj).length;
      setProgress(Math.round((processed / rowsToImport.length) * 100));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setLoading(false);
    return statusObj;
  };

  const apiForType = () => {
    if (importType === IMPORT_TYPES.FEE_UPDATE) return StudentFeeUpdateImportV0;
    if (importType === IMPORT_TYPES.PAYMENT) return StudentPaymentImportV0;
    if (importType === IMPORT_TYPES.PROFILE_UPDATE)
      return StudentProfileUpdateImportV0;
    return StudentImportEnrollOnlyV0;
  };

  const handleStartImport = async () => {
    if (!importType) {
      return SweetAlertShow("error", "Error", "Please select import type");
    }
    if (needsBatchForImport && !batchId) {
      return SweetAlertShow("error", "Error", "Please select batch");
    }
    if (isImporting) return;

    const requiredFields =
      importType === IMPORT_TYPES.PAYMENT
        ? ["registration", "amount", "payment_date", "bank_account_no"]
        : importType === IMPORT_TYPES.FEE_UPDATE
          ? ["registration"]
          : importType === IMPORT_TYPES.PROFILE_UPDATE
            ? ["registration"]
            : ["registration", "name", "mobile"];

    const missingFields = requiredFields.filter(
      (rf) => !mappingRows.some((m) => m.field === rf),
    );
    if (missingFields.length) {
      return SweetAlertShow(
        "error",
        "Missing Mapping",
        missingFields.join(", "),
      );
    }

    const rows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status !== "success",
      );

    if (!rows.length) {
      return SweetAlertShow(
        "error",
        "No Valid Rows",
        "Please fix validation errors",
      );
    }

    setProgress(0);
    setIsImporting(true);

    const finalStatus = await runImport(rows, apiForType());

    const successCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "success",
    ).length;
    const failedCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "error",
    ).length;

    SweetAlertShow(
      "success",
      "Import Completed",
      `Success: ${successCount}, Failed: ${failedCount}`,
    );
    setIsImporting(false);
  };

  const handleRetryFailed = async () => {
    const failedRows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    setIsImporting(true);
    await runImport(failedRows, apiForType());
    setIsImporting(false);
  };

  const handleRetrySingle = async (index) => {
    if (validationErrors[index]?.length) {
      return SweetAlertShow(
        "error",
        "Validation Error",
        validationErrors[index].join(", "),
      );
    }
    setIsImporting(true);
    await runImport([{ index, data: editableData[index] }], apiForType());
    setIsImporting(false);
  };

  const downloadFailedRows = () => {
    const failedRows = editableData
      .map((row, index) => ({
        ...row,
        errors: validationErrors[index].join(", "),
        import_error: importStatus[index]?.message || "",
      }))
      .filter(
        (_, index) =>
          validationErrors[index]?.length ||
          importStatus[index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    const worksheet = XLSX.utils.json_to_sheet(failedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Rows");
    XLSX.writeFile(workbook, "failed_import_rows.xlsx");
  };

  const successCount = Object.values(importStatus).filter(
    (s) => s.status === "success",
  ).length;
  const failedCount = Object.values(importStatus).filter(
    (s) => s.status === "error",
  ).length;
  const validationCount = validationErrors.filter((e) => e.length).length;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-4">
                          <div className="col-sm-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={importType}
                                onChange={(e) => {
                                  setImportType(e.target.value);
                                  setExcelHeaders([]);
                                  setExcelData([]);
                                  setEditableData([]);
                                  setMappingRows([]);
                                  setImportStatus({});
                                  setBatchId("");
                                }}
                              >
                                <option value="">Select Import Type</option>
                                <option value={IMPORT_TYPES.ENROLL_ONLY}>
                                  Student Enroll Only (No Fee)
                                </option>
                                <option value={IMPORT_TYPES.FEE_UPDATE}>
                                  Fee Update (Already Enrolled)
                                </option>
                                <option value={IMPORT_TYPES.FEE_UPDATE_ALL}>
                                  All Students Fee Update (Already Enrolled)
                                </option>
                                <option value={IMPORT_TYPES.PAYMENT}>
                                  Payment Transaction Upload
                                </option>
                                <option value={IMPORT_TYPES.PROFILE_UPDATE}>
                                  Student Profile Update Only
                                </option>
                              </select>
                              <label className="ms-5">Import Type</label>
                            </div>
                          </div>
                          {/* {JSON.stringify(sessions)} */}
                          {importType && (
                            <>
                              <div className="col-sm-2">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="session_id"
                                    onChange={(e) => {
                                      getBatchList(e.target.value);
                                      setSessionId(e.target.value);
                                    }}
                                  >
                                    {sessions?.map((session) => (
                                      <option
                                        key={session?.id}
                                        value={session?.id}
                                        selected={session?.status === "1"}
                                      >
                                        {session?.session} :{" "}
                                        {session?.status === "1"
                                          ? "Active"
                                          : " Inactive"}
                                      </option>
                                    ))}
                                  </select>
                                  <label htmlFor="course">Select session</label>
                                </div>
                              </div>
                              {needsBatchForDownload && (
                                <div className="col-sm-2">
                                  <div className="form-floating form-floating-outline">
                                    <select
                                      className="form-select"
                                      value={batchId}
                                      onChange={(e) =>
                                        setBatchId(e.target.value)
                                      }
                                    >
                                      <option value="">Select Batch</option>
                                      <option value="all">All</option>
                                      {batches?.map((batch) => (
                                        <option key={batch.id} value={batch.id}>
                                          {batch?.title} ({batch?.course?.title}
                                          )
                                        </option>
                                      ))}
                                    </select>
                                    <label className="ms-5">
                                      {importType ===
                                      IMPORT_TYPES.FEE_UPDATE_ALL
                                        ? "Batch (existing enrollment)"
                                        : "Batch to enroll into"}
                                    </label>
                                  </div>
                                </div>
                              )}

                              <div className="col-sm-3">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileUpload}
                                  />
                                  <label className="ms-5">Upload Excel</label>
                                </div>
                              </div>

                              <div className="col-sm-3 text-end">
                                {importType === IMPORT_TYPES.FEE_UPDATE ? (
                                  <button
                                    className="btn btn-info me-2"
                                    onClick={downloadEnrolledTemplate}
                                  >
                                    Download Enrolled Students Sheet {session_id}
                                  </button>
                                ) : importType ===
                                  IMPORT_TYPES.PROFILE_UPDATE ? (
                                  <button
                                    className="btn btn-info me-2"
                                    onClick={downloadProfileTemplate}
                                  >
                                    Download Student Profile Sheet
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-info me-2"
                                    onClick={downloadBlankTemplate}
                                  >
                                    Download Template
                                  </button>
                                )}
                              </div>
                            </>
                          )}

                          <div className="col-12">
                            <div className="d-flex justify-content-end mb-2">
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleAddRow}
                                disabled={!excelHeaders.length}
                              >
                                Add Field
                              </button>
                            </div>

                            <div className="table-responsive">
                              <table className="table table-bordered">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Excel Column</th>
                                    <th>Preview</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {mappingRows?.map((row, index) => (
                                    <tr key={index}>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.field}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "field",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">Select Field</option>
                                          {activeFields?.map((f, i) => (
                                            <option
                                              key={i}
                                              value={f.key}
                                              disabled={
                                                usedFields.includes(f.key) &&
                                                row.field !== f.key
                                              }
                                            >
                                              {f.value}
                                              {readOnlyFields.includes(f.key) &&
                                              importType ===
                                                IMPORT_TYPES.FEE_UPDATE
                                                ? " (info only)"
                                                : ""}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.excel_column}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "excel_column",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select Column
                                          </option>
                                          {excelHeaders.map((col, i) => (
                                            <option key={i} value={col}>
                                              {col}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <input
                                          type="text"
                                          className="form-control p-1"
                                          readOnly
                                          value={
                                            excelSample[row.excel_column] || ""
                                          }
                                        />
                                      </td>
                                      <td className="p-1">
                                        <i
                                          className="ri-delete-bin-line text-danger"
                                          onClick={() => handleRemoveRow(index)}
                                        ></i>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-4">
                        <div className="card bg-success text-white">
                          <div className="card-body">
                            Success : {successCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-danger text-white">
                          <div className="card-body">
                            Failed : {failedCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-warning">
                          <div className="card-body">
                            Validation Errors : {validationCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mt-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5>Excel Preview</h5>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success"
                              onClick={handleStartImport}
                              disabled={isImporting || !editableData.length}
                            >
                              Start Import
                            </button>
                            <button
                              className="btn btn-warning"
                              onClick={handleRetryFailed}
                              disabled={isImporting}
                            >
                              Retry Failed
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={downloadFailedRows}
                            >
                              Download Failed
                            </button>
                          </div>
                        </div>

                        <div className="progress mb-3">
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                          >
                            {progress}%
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-bordered table-hover table-sm">
                            <thead className="table-dark">
                              <tr>
                                <th>SN</th>
                                {activeMappings?.map((m, i) => (
                                  <th key={i}>
                                    {m.field} ({m.excel_column})
                                  </th>
                                ))}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editableData?.map((row, i) => (
                                <tr
                                  key={i}
                                  className={`${
                                    importStatus[i]?.status === "error"
                                      ? "table-danger"
                                      : validationErrors[i]?.length
                                        ? "table-warning"
                                        : ""
                                  }`}
                                >
                                  <td>{i + 1}</td>
                                  {activeMappings.map((m, j) => (
                                    <td key={j} className="p-1">
                                      <input
                                        type="text"
                                        className="form-control p-1"
                                        value={row[m.field] ?? ""}
                                        onChange={(e) =>
                                          handleCellChange(
                                            i,
                                            m.field,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                  <td>
                                    {importStatus[i]?.status === "success" && (
                                      <span className="badge bg-success">
                                        Imported
                                      </span>
                                    )}
                                    {importStatus[i]?.status === "error" && (
                                      <>
                                        <span className="badge bg-danger">
                                          Failed
                                        </span>
                                        <button
                                          className="btn btn-warning btn-sm ms-2"
                                          onClick={() => handleRetrySingle(i)}
                                        >
                                          Retry
                                        </button>
                                        <div
                                          className="text-danger mt-1"
                                          style={{ fontSize: "11px" }}
                                        >
                                          {importStatus[i]?.message}
                                        </div>
                                      </>
                                    )}
                                    {!importStatus[i] && (
                                      <span className="badge bg-secondary">
                                        Pending
                                      </span>
                                    )}
                                    {validationErrors[i]?.length > 0 && (
                                      <div
                                        className="text-danger mt-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {validationErrors[i].join(", ")}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
